import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DaDataResponse } from '@company/const';
import { CompanyService } from '@company/feature';
import { InputAutocompleteComponent, SdModalService, SdvorButtonComponent } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { map } from 'rxjs';

@Component({
  selector: 'sd-add-company-modal',
  imports: [SdvorButtonComponent, InputAutocompleteComponent],
  templateUrl: './add-company-modal.component.html',
  styleUrls: ['./add-company-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCompanyModalComponent implements OnDestroy {
  private companyService = inject(CompanyService);
  private modalService = inject(SdModalService);
  private deviceService = inject(DeviceService);

  protected readonly isMobileSmDown = toSignal(this.deviceService.isMobileSmDown$, { initialValue: false });

  private inputEnteredValue = signal('');
  protected inputSelectedValue = signal<DaDataResponse | undefined>(undefined);

  searchCompanyLoading = toSignal(this.companyService.getSearchCompanyLoading(), { initialValue: false });
  addCompanyLoading = toSignal(this.companyService.getAddCompanyLoading(), { initialValue: false });
  userCompaniesLoading = toSignal(this.companyService.getUserCompaniesLoading(), { initialValue: false });

  searchResult = toSignal(
    this.companyService
      .getSearchCompanyResult()
      .pipe(map((items) => items.map((item) => ({ label: item.name, value: item })))),
    { initialValue: [] },
  );

  constructor() {
    effect((onCleanup) => {
      const query = this.inputEnteredValue();

      if (query.length <= 2) return;

      const timeoutId = setTimeout(() => {
        this.companyService.searchCompany(query);
      }, 600);

      onCleanup(() => clearTimeout(timeoutId));
    });
  }

  onInputChange(value: string): void {
    this.inputEnteredValue.set(value);
  }

  onInputSelect(value: DaDataResponse): void {
    this.inputSelectedValue.set(value);
  }

  onAddCompanyBtnClick(): void {
    const data = this.inputSelectedValue();
    if (!data) return;

    this.modalService.closeActiveModal();
    this.companyService.addCompany({
      name: data.name,
      inn: data.vatID,
      kpp: data.debtorKPP,
      ogrn: data.debtorOGRN,
      address: {
        ...(data.address || {}),
        formattedAddress: data.contactAddress,
      },
    });
  }

  ngOnDestroy(): void {
    this.companyService.resetSearchCompanyResult();
  }
}
